import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
  serverTimestamp
} from 'firebase/firestore';
import {
  createPropertyCustomItemId,
  normalizePropertyDetailsCustom
} from '../constants/propertyDetails';

const SETTINGS_COLLECTION = 'settings';
const CATALOG_DOC_ID = 'propertyDetailsCustom';

function catalogDocRef(db) {
  return doc(db, SETTINGS_COLLECTION, CATALOG_DOC_ID);
}

export function mergePropertyDetailsCustomCatalog(globalCatalog = [], legacyCatalog = []) {
  const byId = new Map();
  normalizePropertyDetailsCustom(globalCatalog).forEach((item) => byId.set(item.id, item));
  normalizePropertyDetailsCustom(legacyCatalog).forEach((item) => {
    if (!byId.has(item.id)) byId.set(item.id, item);
  });
  return Array.from(byId.values());
}

export async function fetchGlobalPropertyDetailsCustomOptions() {
  const db = getFirestore();
  const snap = await getDoc(catalogDocRef(db));
  if (!snap.exists()) return [];
  return normalizePropertyDetailsCustom(snap.data()?.options);
}

export async function saveGlobalPropertyDetailsCustomOptions(options) {
  const db = getFirestore();
  const normalized = normalizePropertyDetailsCustom(options);
  await setDoc(
    catalogDocRef(db),
    {
      options: normalized,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
  return normalized;
}

export async function addGlobalPropertyDetailsCustomOption({ topic, label }) {
  const trimmed = typeof label === 'string' ? label.trim() : '';
  if (!trimmed) return null;

  const current = await fetchGlobalPropertyDetailsCustomOptions();
  const duplicate = current.find(
    (item) => item.topic === topic && item.label.toLowerCase() === trimmed.toLowerCase()
  );
  if (duplicate) return duplicate;

  const newItem = {
    id: createPropertyCustomItemId(),
    topic,
    label: trimmed
  };
  await saveGlobalPropertyDetailsCustomOptions([...current, newItem]);
  return newItem;
}

export async function removeGlobalPropertyDetailsCustomOption(id) {
  const current = await fetchGlobalPropertyDetailsCustomOptions();
  const next = current.filter((item) => item.id !== id);
  await saveGlobalPropertyDetailsCustomOptions(next);
  return next;
}

export async function getPropertyCustomOptionUsageMap() {
  const keyPrefix = 'custom_';
  const db = getFirestore();
  const snap = await getDocs(collection(db, 'properties'));
  const usage = {};
  snap.forEach((docSnap) => {
    const d = docSnap.data()?.details || {};
    Object.entries(d).forEach(([key, value]) => {
      if (value === true && key.startsWith(keyPrefix)) {
        const id = key.slice(keyPrefix.length);
        usage[id] = (usage[id] || 0) + 1;
      }
    });
  });
  return usage;
}

export async function migrateLegacyPropertyDetailsCustomToGlobal(legacyItems = []) {
  const legacy = normalizePropertyDetailsCustom(legacyItems);
  if (legacy.length === 0) return fetchGlobalPropertyDetailsCustomOptions();

  const current = await fetchGlobalPropertyDetailsCustomOptions();
  const existingIds = new Set(current.map((item) => item.id));
  const toAdd = legacy.filter((item) => !existingIds.has(item.id));
  if (toAdd.length === 0) return current;

  return saveGlobalPropertyDetailsCustomOptions([...current, ...toAdd]);
}
